/* eslint-disable no-console */
import { datadogLogs } from '@datadog/browser-logs';
import { captureException } from '@sentry/react';
import { isFeatureEnabled } from 'common/utils';

// Supported error patterns:
// - logger.error(error)                    -> error only (accepts unknown)
// - logger.error('message')                -> message only
// - logger.error('message', error)         -> message + error
// - logger.error('message', context)       -> message + context
// - logger.error(error, context)           -> error + context
// - logger.error('message', context, error) -> message + context + error

type ErrorArg = string | Error | unknown;
type Context = Record<string, unknown>;

const toError = (value: unknown): Error => {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  return new Error(String(value));
};

const isContext = (value: unknown): value is Context =>
  typeof value === 'object' && value !== null && !(value instanceof Error);

const wrapContext = (ctx: Context | undefined): { ctx: Context } | undefined => (ctx ? { ctx } : undefined);

const datadogLogger = {
  debug: (message: string, context?: Context) => {
    datadogLogs.logger.debug(message, wrapContext(context));
  },
  error: (messageOrError: ErrorArg, contextOrError?: Context | ErrorArg, error?: ErrorArg) => {
    let message: string;
    let err: Error;
    let ctx: Context | undefined;

    if (typeof messageOrError !== 'string') {
      // logger.error(error) or logger.error(error, context)
      err = toError(messageOrError);
      message = err.message;
      ctx = isContext(contextOrError) ? contextOrError : undefined;
    } else {
      message = messageOrError;
      if (!isContext(contextOrError) && contextOrError !== undefined) {
        // logger.error('message', error) - wrap with new Error, chain original as cause
        err = new Error(message, { cause: toError(contextOrError) });
      } else {
        // logger.error('message', context) or logger.error('message', context, error)
        ctx = contextOrError;
        // Create Error from message, chain original error as cause if provided
        err = error ? new Error(message, { cause: toError(error) }) : new Error(message);
      }
    }

    // Log to Datadog (expects: message, context, error)
    datadogLogs.logger.error(message, wrapContext(ctx), err);

    // Also capture in Sentry for error tracking
    if (isFeatureEnabled('SentryEnabled')) {
      captureException(err, { level: 'error', extra: ctx });
    }
  },
  info: (message: string, context?: Context) => {
    datadogLogs.logger.info(message, wrapContext(context));
  },
  warn: (message: string, context?: Context) => {
    datadogLogs.logger.warn(message, wrapContext(context));
  },
};

const formatContext = (ctx?: Context): string => (ctx ? ` ${JSON.stringify(ctx, null, 2)}` : '');

const localLogger = {
  debug: (message: string, context?: Context) => {
    console.debug(message + formatContext(context));
  },
  error: (messageOrError: ErrorArg, contextOrError?: Context | ErrorArg, error?: ErrorArg) => {
    if (typeof messageOrError !== 'string') {
      const ctx = isContext(contextOrError) ? contextOrError : undefined;
      console.error('%s', toError(messageOrError).message + formatContext(ctx), toError(messageOrError));
    } else if (!isContext(contextOrError) && contextOrError !== undefined) {
      console.error(messageOrError, toError(contextOrError));
    } else {
      const ctx = contextOrError;
      console.error(messageOrError + formatContext(ctx), ...(error ? [toError(error)] : []));
    }
  },
  info: (message: string, context?: Context) => {
    console.info(message + formatContext(context));
  },
  warn: (message: string, context?: Context) => {
    console.warn(message + formatContext(context));
  },
};

export const logger =
  isFeatureEnabled('DatadogEnabled') && process.env.NODE_ENV !== 'test' ? datadogLogger : localLogger;
